import React from 'react'

import { useTheme } from '@mui/material'

import TableCell from 'components_new/atoms/Table/TableCell'
import Text from 'components_new/atoms/Text'
import Link from 'components_new/atoms/Link'

import { ellipseLabel, getCellScaleSx, getStickyCellSx } from '../utils'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'

import { TableRow } from 'components_new/molecules/Table'

const getPaddingLeft = (
  segmentDepth: number,
  longestPath: number,
  hasChildren: boolean
) => {
  let paddingLeft = 0

  if (longestPath > 0) {
    paddingLeft = segmentDepth * 2

    // Add extra padding if no expand button to compensate for IconButton space.
    if (!hasChildren) {
      paddingLeft += 2
    }
  }

  return paddingLeft
}

interface SegmentCellProps {
  divider: boolean
  longestPath: number
  expandedPathSet: Set<string>
  item: TableRow
  maxChars: number | null
  measureRef?: (cell: HTMLTableCellElement | null) => void
  scaleFactor: number
  onToggleExpanded: (path: string, expanded: boolean) => void
  variant: 'body' | 'footer' | 'head'
}

const SegmentCell = (props: SegmentCellProps) => {
  const {
    divider,
    longestPath,
    expandedPathSet,
    item,
    maxChars,
    measureRef,
    scaleFactor,
    onToggleExpanded,
    variant
  } = props

  const { path, segmentDepth, hasChildren } = item.label

  const link = item.link ?? null
  const allowOnClick = !!item.onCellClick

  const handleClick = item.onCellClick
    ? (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        item.onCellClick?.(event)
      }
    : undefined
  const paddingLeft = getPaddingLeft(segmentDepth, longestPath, hasChildren)
  const isExpanded = hasChildren && expandedPathSet.has(path)

  const theme = useTheme()

  const segmentTextComponent = (
    <Text
      color="inherit"
      component="span"
      fontSize="inherit"
      fontWeight={hasChildren ? 'bold' : 'medium'}
      variant="body2"
      sx={{
        paddingLeft: paddingLeft,
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {hasChildren ? (
        <IconButton
          onClick={(e) => {
            e.stopPropagation()
            onToggleExpanded(path, !isExpanded)
          }}
          sx={{ mr: 1, fontSize: 'inherit' }}
          size={'small'}
        >
          <Icon
            name={isExpanded ? 'IndeterminateCheckBox' : 'AddBox'}
            fontSize="inherit"
          />
        </IconButton>
      ) : null}
      {ellipseLabel(item.label.display_label, maxChars)}
    </Text>
  )

  return (
    <>
      <TableCell
        onClick={allowOnClick ? handleClick : undefined}
        ref={measureRef}
        sx={{
          borderRight: divider ? '1px solid' : undefined,
          '&:last-child': { borderRight: 0 },
          borderRightColor: 'divider',
          cursor: allowOnClick ? 'pointer' : undefined,
          whiteSpace: 'nowrap',
          width: '1%', // For auto sized, fit content cells. (Hacker).
          ...getStickyCellSx(theme, variant !== 'body', 0),
          '&:hover': allowOnClick
            ? {
                backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
                color: 'text.solid',
                textDecoration: 'underline'
              }
            : undefined,
          ...getCellScaleSx(scaleFactor)
        }}
        variant={variant}
      >
        {link ? (
          <Link
            underline="none"
            newTab={true}
            color="inherit"
            sx={{
              '&:hover': { color: 'inherit' },
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {segmentTextComponent}
          </Link>
        ) : (
          segmentTextComponent
        )}
      </TableCell>
    </>
  )
}

export default SegmentCell
